/**
 * API Key Management Service
 * 
 * Handles encrypted API key operations:
 * - List API keys with masked values
 * - Reveal API keys (decrypt with rate limiting)
 * - Regenerate API keys
 * - Manage IP whitelist
 */

import axiosInstance from './axios-instance';

const API_ENDPOINT = '/api/v2/business/settings';

/**
 * API Key interfaces
 */
export interface ApiKey {
  id: string;
  key_id: string;
  key_id_masked: string;
  name: string;
  description: string | null;
  package_level: 'basic' | 'premium' | 'enterprise' | 'owner';
  is_active: boolean;
  last_used: string | null;
  expires_at: string | null;
  permissions: string[];
  ip_whitelist: string[];
  ip_whitelist_count: number;
  reveal_count: number;
  last_revealed_at: string | null;
  has_encrypted_key: boolean;
  can_reveal: boolean;
  created_at: string;
  updated_at: string;
}

export interface RevealedKey {
  key: string;
  key_id: string;
  reveal_count: number;
  revealed_at: string;
  hide_after: string;
}

export interface RegeneratedKey {
  key: string;
  key_id: string;
  id: string;
  name: string;
  description: string | null;
  package_level: string;
  permissions: string[];
  ip_whitelist: string[];
  created_at: string;
  warning: string;
}

export interface IpWhitelistData {
  ip_whitelist: string[];
  count: number;
  max_entries: number;
  note: string | null;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

/**
 * List all API keys for authenticated customer
 * Returns masked keys with metadata
 * 
 * @returns Promise with array of API keys
 */
export async function listApiKeys(): Promise<ApiKey[]> {
  try {
    const response = await axiosInstance.get<ApiResponse<{ apiKeys: ApiKey[]; total: number }>>(
      `${API_ENDPOINT}/api-keys`
    );
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to fetch API keys');
    }
    
    return response.data.data.apiKeys;
  } catch (error: any) {
    const message = error.response?.data?.message || error.message;
    throw new Error(`Failed to list API keys: ${message}`);
  }
}

/**
 * Reveal API key (decrypt and return plain key)
 * Rate limited to 10 reveals per hour
 * 
 * @param keyId - UUID of the API key
 * @returns Promise with revealed key data
 */
export async function revealApiKey(keyId: string): Promise<RevealedKey> {
  try {
    const response = await axiosInstance.post<ApiResponse<RevealedKey>>(
      `${API_ENDPOINT}/api-keys/${keyId}/reveal`,
      {}
    );
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to reveal API key');
    }
    
    return response.data.data;
  } catch (error: any) {
    const statusCode = error.response?.status;
    const message = error.response?.data?.message || error.message;
    
    if (statusCode === 429) {
      const resetAt = error.response?.data?.data?.resetAt;
      throw new Error(`Rate limit exceeded. ${message}${resetAt ? ` Try again after ${new Date(resetAt).toLocaleTimeString()}` : ''}`);
    } else if (statusCode === 400) {
      throw new Error('This API key needs to be regenerated before it can be revealed.');
    } else if (statusCode === 404) {
      throw new Error('API key not found.');
    } else if (statusCode === 403) {
      throw new Error('Access denied. You do not have permission to reveal this key.');
    }
    
    throw new Error(`Failed to reveal API key: ${message}`);
  }
}

/**
 * Regenerate API key
 * Deactivates old key and creates new encrypted key
 * 
 * @param keyId - UUID of the API key to regenerate
 * @returns Promise with new key data
 */
export async function regenerateApiKey(keyId: string): Promise<RegeneratedKey> {
  try {
    const response = await axiosInstance.post<ApiResponse<RegeneratedKey>>(
      `${API_ENDPOINT}/api-keys/${keyId}/regenerate`,
      {}
    );
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to regenerate API key');
    }
    
    return response.data.data;
  } catch (error: any) {
    const message = error.response?.data?.message || error.message;
    throw new Error(`Failed to regenerate API key: ${message}`);
  }
}

/**
 * Get IP whitelist for API key
 * 
 * @param keyId - UUID of the API key
 * @returns Promise with IP whitelist data
 */
export async function getIpWhitelist(keyId: string): Promise<IpWhitelistData> {
  try {
    const response = await axiosInstance.get<ApiResponse<IpWhitelistData>>(
      `${API_ENDPOINT}/api-keys/${keyId}/ip-whitelist`
    );
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to get IP whitelist');
    }
    
    return response.data.data;
  } catch (error: any) {
    const message = error.response?.data?.message || error.message;
    throw new Error(`Failed to get IP whitelist: ${message}`);
  }
}

/**
 * Add IP to whitelist
 * Maximum 10 IP addresses allowed
 * 
 * @param keyId - UUID of the API key
 * @param ip - IP address to add (IPv4 or IPv6)
 * @returns Promise with updated whitelist
 */
export async function addIpToWhitelist(keyId: string, ip: string): Promise<IpWhitelistData> {
  try {
    const response = await axiosInstance.post<ApiResponse<IpWhitelistData>>(
      `${API_ENDPOINT}/api-keys/${keyId}/ip-whitelist`,
      { ip }
    );
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to add IP to whitelist');
    }
    
    return response.data.data;
  } catch (error: any) {
    const statusCode = error.response?.status;
    const message = error.response?.data?.message || error.message;
    
    if (statusCode === 400) {
      throw new Error(message);
    }
    
    throw new Error(`Failed to add IP to whitelist: ${message}`);
  }
}

/**
 * Remove IP from whitelist
 * 
 * @param keyId - UUID of the API key
 * @param ip - IP address to remove
 * @returns Promise with updated whitelist
 */
export async function removeIpFromWhitelist(keyId: string, ip: string): Promise<IpWhitelistData> {
  try {
    const response = await axiosInstance.delete<ApiResponse<IpWhitelistData>>(
      `${API_ENDPOINT}/api-keys/${keyId}/ip-whitelist/${encodeURIComponent(ip)}`
    );
    
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to remove IP from whitelist');
    }
    
    return response.data.data;
  } catch (error: any) {
    const message = error.response?.data?.message || error.message;
    throw new Error(`Failed to remove IP from whitelist: ${message}`);
  }
}
